import React, { useState, useEffect, useCallback } from 'react';
import OrbitLayout from '../components/OrbitLayout';
import { useLanguage } from '../context/LanguageContext';
import { useOrbitAuth } from '../context/OrbitAuthContext';
import { Plus, Trash2, Edit3, ExternalLink, Pause, Play, Home, Briefcase, GraduationCap, X } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_CONFIG = {
  accommodation: { icon: Home, colorBg: 'bg-[var(--ls-terracotta)]/10', colorText: 'text-[var(--ls-terracotta)]', colorBorder: 'border-[var(--ls-terracotta)]/20' },
  service: { icon: Briefcase, colorBg: 'bg-[var(--ls-forest)]/10', colorText: 'text-[var(--ls-forest)]', colorBorder: 'border-[var(--ls-forest)]/20' },
  training: { icon: GraduationCap, colorBg: 'bg-[var(--ls-gold)]/10', colorText: 'text-[var(--ls-gold)]', colorBorder: 'border-[var(--ls-gold)]/20' },
};

const CATEGORIES = {
  accommodation: ['chambre', 'appartement', 'maison', 'chalet', 'autre'],
  service: ['cuisine', 'beaute', 'sante', 'reparation', 'menage', 'photo', 'musique', 'traiteur', 'jardinage', 'coaching', 'autre'],
  training: ['langues', 'informatique', 'arts', 'cuisine', 'fitness', 'affaires', 'developpement-personnel', 'autre'],
};

const MyListingsPage = () => {
  const { language } = useLanguage();
  const { token } = useOrbitAuth();
  const t = (fr, en) => language === 'fr' ? fr : en;

  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    listing_type: 'service', title_fr: '', title_en: '', description_fr: '', description_en: '',
    category: '', price_indication: '', city: '', province_code: '', postal_code: '',
    is_remote: false, external_link: '',
  });

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/orbit/listings/my`, { headers });
      if (res.ok) { const data = await res.json(); setListings(data.listings); }
    } catch { toast.error(t('Erreur', 'Error')); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchListings(); }, [fetchListings]);

  const resetForm = () => {
    setForm({ listing_type: 'service', title_fr: '', title_en: '', description_fr: '', description_en: '',
      category: '', price_indication: '', city: '', province_code: '', postal_code: '',
      is_remote: false, external_link: '' });
    setEditId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title_fr || !form.description_fr) { toast.error(t('Titre et description requis', 'Title and description required')); return; }
    try {
      const url = editId ? `${API}/api/orbit/listings/${editId}` : `${API}/api/orbit/listings`;
      const method = editId ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify(form) });
      if (!res.ok) throw new Error();
      toast.success(editId ? t('Annonce mise a jour !', 'Listing updated!') : t('Annonce publiee !', 'Listing published!'));
      setShowForm(false); resetForm(); fetchListings();
    } catch { toast.error(t('Erreur', 'Error')); }
  };

  const handleEdit = (listing) => {
    setForm({
      listing_type: listing.listing_type, title_fr: listing.title_fr || '', title_en: listing.title_en || '',
      description_fr: listing.description_fr || '', description_en: listing.description_en || '',
      category: listing.category || '', price_indication: listing.price_indication || '',
      city: listing.city || '', province_code: listing.province_code || '', postal_code: listing.postal_code || '',
      is_remote: listing.is_remote || false, external_link: listing.external_link || '',
    });
    setEditId(listing.listing_id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('Supprimer cette annonce ?', 'Delete this listing?'))) return;
    try {
      await fetch(`${API}/api/orbit/listings/${id}`, { method: 'DELETE', headers });
      toast.success(t('Annonce supprimee', 'Listing deleted'));
      fetchListings();
    } catch { toast.error(t('Erreur', 'Error')); }
  };

  const toggleStatus = async (listing) => {
    const newStatus = listing.status === 'published' ? 'paused' : 'published';
    try {
      await fetch(`${API}/api/orbit/listings/${listing.listing_id}`, {
        method: 'PUT', headers, body: JSON.stringify({ status: newStatus })
      });
      toast.success(newStatus === 'published' ? t('Annonce activee', 'Listing activated') : t('Annonce pausee', 'Listing paused'));
      fetchListings();
    } catch { toast.error(t('Erreur', 'Error')); }
  };

  const typeLabels = {
    accommodation: t('Hebergement', 'Accommodation'),
    service: t('Service', 'Service'),
    training: t('Formation', 'Training'),
  };

  const inputClass = "w-full px-4 py-2.5 rounded-xl border border-[hsl(40,20%,88%)] bg-white text-[var(--ls-text)] text-sm focus:outline-none focus:ring-2 focus:ring-[var(--ls-terracotta)]/20 focus:border-[var(--ls-terracotta)] transition-all";

  return (
    <OrbitLayout>
      <div className="space-y-6" data-testid="my-listings-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">{t('Mes Annonces', 'My Listings')}</h1>
            <p className="text-gray-300 mt-1">{t('Proposez vos services et hebergements aux membres', 'Offer your services and accommodations to members')}</p>
          </div>
          <button onClick={() => { resetForm(); setShowForm(true); }}
            className="flex items-center gap-2 px-5 py-2.5 bg-[var(--ls-terracotta)] text-white rounded-full font-semibold text-sm hover:bg-[var(--ls-terracotta-dark)] transition-colors shadow-lg shadow-[var(--ls-terracotta)]/20"
            data-testid="new-listing-btn">
            <Plus className="w-4 h-4" />{t('Nouvelle annonce', 'New listing')}
          </button>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" data-testid="listing-form-modal">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white rounded-t-3xl border-b border-[hsl(40,20%,88%)] px-6 py-4 flex items-center justify-between">
                <h2 className="font-heading font-bold text-[var(--ls-text)]">
                  {editId ? t('Modifier l\'annonce', 'Edit listing') : t('Nouvelle annonce', 'New listing')}
                </h2>
                <button onClick={() => { setShowForm(false); resetForm(); }}
                  className="p-2 hover:bg-[var(--ls-sand-dark)] rounded-lg" data-testid="close-form-btn"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                {/* Type selector */}
                <div>
                  <label className="block text-xs font-medium text-[var(--ls-text-muted)] mb-2">{t('Type d\'annonce', 'Listing type')}</label>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(TYPE_CONFIG).map(([type, cfg]) => (
                      <button key={type} type="button" onClick={() => setForm(f => ({ ...f, listing_type: type, category: '' }))}
                        className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all text-xs font-semibold ${
                          form.listing_type === type ? `${cfg.colorBg} ${cfg.colorText} ${cfg.colorBorder}` : 'border-[hsl(40,20%,88%)] text-[var(--ls-text-muted)]'
                        }`} data-testid={`type-${type}`}>
                        <cfg.icon className="w-5 h-5" />{typeLabels[type]}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-[var(--ls-text-muted)] mb-1">{t('Titre (Francais)', 'Title (French)')} *</label>
                  <input type="text" value={form.title_fr} onChange={e => setForm(f => ({ ...f, title_fr: e.target.value }))}
                    required className={inputClass} data-testid="listing-title-fr" placeholder={t('Ex: Cours de cuisine italienne', 'Ex: Italian cooking class')} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--ls-text-muted)] mb-1">{t('Titre (Anglais)', 'Title (English)')}</label>
                  <input type="text" value={form.title_en} onChange={e => setForm(f => ({ ...f, title_en: e.target.value }))}
                    className={inputClass} data-testid="listing-title-en" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--ls-text-muted)] mb-1">{t('Description (Francais)', 'Description (French)')} *</label>
                  <textarea value={form.description_fr} onChange={e => setForm(f => ({ ...f, description_fr: e.target.value }))}
                    required rows={3} className={`${inputClass} resize-none`} data-testid="listing-desc-fr" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--ls-text-muted)] mb-1">{t('Description (Anglais)', 'Description (English)')}</label>
                  <textarea value={form.description_en} onChange={e => setForm(f => ({ ...f, description_en: e.target.value }))}
                    rows={3} className={`${inputClass} resize-none`} data-testid="listing-desc-en" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-[var(--ls-text-muted)] mb-1">{t('Categorie', 'Category')}</label>
                    <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                      className={inputClass} data-testid="listing-category">
                      <option value="">{t('Choisir...', 'Choose...')}</option>
                      {(CATEGORIES[form.listing_type] || []).map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[var(--ls-text-muted)] mb-1">{t('Prix indicatif', 'Indicative price')}</label>
                    <input type="text" value={form.price_indication} onChange={e => setForm(f => ({ ...f, price_indication: e.target.value }))}
                      className={inputClass} placeholder="Ex: 45$/h" data-testid="listing-price" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-[var(--ls-text-muted)] mb-1">{t('Ville', 'City')}</label>
                    <input type="text" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                      className={inputClass} data-testid="listing-city" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[var(--ls-text-muted)] mb-1">{t('Code postal', 'Postal code')}</label>
                    <input type="text" value={form.postal_code} onChange={e => setForm(f => ({ ...f, postal_code: e.target.value }))}
                      className={inputClass} data-testid="listing-postal" />
                  </div>
                </div>

                {/* Remote toggle */}
                {form.listing_type !== 'accommodation' && (
                  <div className="flex items-center justify-between p-3 bg-[var(--ls-sand-dark)] rounded-xl">
                    <span className="text-sm text-[var(--ls-text)]">{t('Disponible a distance', 'Available remotely')}</span>
                    <button type="button" onClick={() => setForm(f => ({ ...f, is_remote: !f.is_remote }))}
                      className={`relative w-11 h-6 rounded-full transition-colors ${form.is_remote ? 'bg-[var(--ls-forest)]' : 'bg-gray-200'}`}
                      data-testid="toggle-remote">
                      <div className="absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform"
                        style={{ left: '2px', transform: form.is_remote ? 'translateX(22px)' : 'translateX(0)' }} />
                    </button>
                  </div>
                )}

                {/* External link */}
                <div>
                  <label className="block text-xs font-medium text-[var(--ls-text-muted)] mb-1 flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" />{t('Lien externe (Calendly, site web, etc.)', 'External link (Calendly, website, etc.)')}
                  </label>
                  <input type="url" value={form.external_link} onChange={e => setForm(f => ({ ...f, external_link: e.target.value }))}
                    className={inputClass} placeholder="https://" data-testid="listing-external-link" />
                  <p className="text-[10px] text-[var(--ls-text-muted)] mt-1">
                    {t('Les transactions se font directement entre vous et le membre, en dehors de LocalSite.Club',
                      'Transactions happen directly between you and the member, outside of LocalSite.Club')}
                  </p>
                </div>

                <button type="submit"
                  className="w-full py-3 rounded-full bg-[var(--ls-terracotta)] text-white font-semibold hover:bg-[var(--ls-terracotta-dark)] transition-colors shadow-lg shadow-[var(--ls-terracotta)]/20"
                  data-testid="submit-listing-btn">
                  {editId ? t('Mettre a jour', 'Update') : t('Publier l\'annonce', 'Publish listing')}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Listings */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-3 border-[var(--ls-terracotta)] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-16 bg-white border border-[hsl(40,20%,88%)] rounded-2xl">
            <Briefcase className="w-14 h-14 text-[var(--ls-text-muted)]/20 mx-auto mb-4" />
            <h3 className="font-heading font-bold text-[var(--ls-text)] mb-1">{t('Aucune annonce', 'No listings')}</h3>
            <p className="text-sm text-[var(--ls-text-muted)] mb-4">{t('Publiez votre premier service, hebergement ou formation !', 'Publish your first service, accommodation or training!')}</p>
            <button onClick={() => { resetForm(); setShowForm(true); }}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[var(--ls-terracotta)] text-white rounded-full font-semibold text-sm">
              <Plus className="w-4 h-4" />{t('Creer une annonce', 'Create a listing')}
            </button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="listings-list">
            {listings.map(listing => {
              const cfg = TYPE_CONFIG[listing.listing_type] || TYPE_CONFIG.service;
              const Icon = cfg.icon;
              return (
                <div key={listing.listing_id} className={`bg-white border border-[hsl(40,20%,88%)] rounded-2xl p-5 hover:shadow-md transition-all ${listing.status === 'paused' ? 'opacity-60' : ''}`}
                  data-testid={`listing-${listing.listing_id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className={`w-10 h-10 rounded-xl ${cfg.colorBg} flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`w-5 h-5 ${cfg.colorText}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <h3 className="font-heading font-bold text-[var(--ls-text)] truncate">
                            {language === 'fr' ? listing.title_fr : (listing.title_en || listing.title_fr)}
                          </h3>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${cfg.colorBg} ${cfg.colorText}`}>
                            {typeLabels[listing.listing_type]}
                          </span>
                          {listing.status === 'paused' && (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-500">{t('Pause', 'Paused')}</span>
                          )}
                          {listing.is_remote && (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-600">{t('A distance', 'Remote')}</span>
                          )}
                        </div>
                        <p className="text-sm text-[var(--ls-text-muted)] line-clamp-1">
                          {language === 'fr' ? listing.description_fr : (listing.description_en || listing.description_fr)}
                        </p>
                        <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-[var(--ls-text-muted)]">
                          {listing.city && <span>{listing.city}</span>}
                          {listing.price_indication && <span className="font-semibold text-[var(--ls-text)]">{listing.price_indication}</span>}
                          {listing.category && <span className="px-2 py-0.5 bg-[var(--ls-sand-dark)] rounded-md">{listing.category}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {listing.external_link && (
                        <a href={listing.external_link} target="_blank" rel="noopener noreferrer"
                          className="p-2 rounded-lg hover:bg-[var(--ls-sand-dark)] text-[var(--ls-text-muted)]" title={t('Lien externe', 'External link')}>
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                      <button onClick={() => toggleStatus(listing)}
                        className="p-2 rounded-lg hover:bg-[var(--ls-sand-dark)] text-[var(--ls-text-muted)]"
                        title={listing.status === 'published' ? t('Mettre en pause', 'Pause') : t('Activer', 'Activate')}>
                        {listing.status === 'published' ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                      </button>
                      <button onClick={() => handleEdit(listing)} className="p-2 rounded-lg hover:bg-[var(--ls-sand-dark)] text-[var(--ls-text-muted)]">
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(listing.listing_id)} className="p-2 rounded-lg hover:bg-red-50 text-red-400">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </OrbitLayout>
  );
};

export default MyListingsPage;
